import { Router } from "@angular/router";
import { Component, OnInit } from "@angular/core";
import { VngTableDataSource, VngPageEvent, VngTourService } from "@vengreso/angular-components";
import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { PersonaAiApiService } from "src/app/core/core-services/services/persona-ai-api.service";
import { LoadingService } from "src/app/core/core-services/services/loader.service";
import { DialogService } from "src/app/core/core-services/services/dialog.service";
import { WarningDialogComponent } from "src/app/core/core-services/warning-dialog/warning-dialog.component";
import { NotificationService } from "src/app/core/core-services/services/notification.service";
import { NgbPopoverConfig } from "@ng-bootstrap/ng-bootstrap";
import { modalStepIndex } from "src/app/core/core-services/services/onboarding-progress.service";
import { GlobalHookService } from "src/app/global-hook.service";
import { UserPersona } from "src/app/core/models/persona.model";

export type UserPersonaQuota = {
  created: number;
  quota: number;
  remaining: number;
};

function isUserPersonaQuota(value: any): value is UserPersonaQuota {
  return (
    !!value &&
    typeof value.created === "number" &&
    typeof value.quota === "number" &&
    typeof value.remaining === "number"
  );
}

@Component({
    selector: "app-user-persona",
    templateUrl: "./user-persona.component.html",
    styleUrls: ["./user-persona.component.scss"],
    standalone: false
})
export class UserPersonaComponent implements OnInit {
  search: string;
  order?: "name" | "type" | "created_at";
  updating: boolean = false;
  dataSource: VngTableDataSource<UserPersona> = new VngTableDataSource();
  pagedRows: UserPersona[] = [];
  loading: boolean = false;
  loadingGetUsers: boolean = false;
  loadingQuota: boolean = false;
  // Walk-me step13 bookkeeping — see startPersonaTourWhenLoaded() and the
  // end$ subscription in ngOnInit.
  private tourStepsReady = false;
  private tourStarted = false;
  private tourExitAction: "persona" | null = null;
  /**
   * Undefined until a well-formed quota answer arrives, and again if the quota
   * request fails (issue #591: a failed quota call used to leave this
   * undefined while the template still dereferenced it, which threw during
   * change detection and left "Add Persona" stuck disabled). The header chip
   * only renders when it is set; flymsg-be's create gate stays the authority.
   */
  userPersonaQuota?: UserPersonaQuota;
  loaderId: string = "settingsPersonaTable";
  personas: UserPersona[] = [];
  displayedColumns: string[] = ["name", "type", "actions"];
  itemsPerPage = 10;
  currentPage = 1; // 1-based (VngPageEvent.page), unlike VngTableDataSource's own 0-based paginator.pageIndex — see slice() below

  orderByOptions: { label: string; value: "name" | "type" | "created_at" }[] = [
    { label: "Creation Date", value: "created_at" },
    { label: "Name", value: "name" },
    { label: "Type", value: "type" },
  ];

  constructor(
    private readonly router: Router,
    private readonly dataService: DataService,
    private readonly personaAiApiService: PersonaAiApiService,
    private readonly dialog: DialogService,
    private readonly loadingService: LoadingService,
    private readonly notification: NotificationService,
    private readonly global: GlobalHookService,
    private readonly tourService: VngTourService,
    config: NgbPopoverConfig
  ) {
    config.container = "body";
    config.placement = ["top"];
    config.autoClose = true;
    config.animation = true;

    this.loadingService.start(this.loaderId);
  }

  ngOnInit(): void {
    if (this.global.onboardingActive) {
      setTimeout(() => {
        // C3-15: the trailing "step13-end" virtual step from the old
        // Material-menu tour library's array is dropped — it had no matching
        // anchor element anywhere (dead per the original library too) and, under
        // vng-tour's stricter "anchor must exist to render/advance" contract,
        // would leave the tour stuck on a lingering popover. Both nextBtnTitle
        // and endBtnTitle were already "Got it!" on step13, confirming the
        // intent was for it to be the actual last step — dropping step13-end
        // makes that true (isLast) so only the end button renders, and
        // "Skip" (prev) still sequentially lands on step13-initial exactly
        // as before.
        this.tourService.initialize([
          {
            anchorId: "step13-initial",
            content: " ",
            title: " ",
            enableBackdrop: true,
            disablePageScrolling: true,
            nextOnAnchorClick: true,
            placement: {
              xPosition: "before",
              yPosition: "above",
            },
          },
          {
            anchorId: "step13",
            stepId: "step13",
            content:
              "Give FlyMSG AI a unique voice. Click here to define how you want your LinkedIn posts and comments to sound.",
            title: "Create Your Persona!",
            enableBackdrop: true,
            disablePageScrolling: true,
            // No nextOnAnchorClick: goToNewPersona() ends the tour itself, so
            // the anchor needs exactly one handler and there is no race over
            // which of the two listeners on the button runs first.
            prevBtnTitle: "Skip",
            endBtnTitle: "Got it!",
            placement: {
              xPosition: "before",
              yPosition: "above",
            },
            popoverClass: "fms-tour-arrow fms-tour-arrow--bottom-end",
          },
        ]);

        this.tourService.stepShow$.subscribe((step) => {
          if (step.anchorId === "step13-initial") {
            // "Skip" lands here (prev from step13); this is the decline path.
            this.tourExitAction = null;
            // Reopen on the NEXT step (LinkedIn AI Comment Generator) — issue #542.
            this.global.components.global.startModal(modalStepIndex("linkedin_ai_comment_generator"));

            setTimeout(() => {
              this.tourService.end();
            }, 1);
            return;
          }

          if (step.anchorId === "step13") {
            this.tourExitAction = "persona";
          }
        });

        // "Got it!" is the step's affirmative answer — "Skip" is the way out —
        // so it has to do what the copy says ("Click here to define how you
        // want your LinkedIn posts...") rather than just dismiss the popover.
        // vng-tour has no per-step end callback, so the intent is carried in
        // tourExitAction: armed while step13 is on screen, cleared by the Skip
        // path above and by goToNewPersona() itself (so clicking the real "Add
        // Persona" button doesn't navigate twice).
        this.tourService.end$.subscribe(() => {
          const action = this.tourExitAction;
          this.tourExitAction = null;
          if (action === "persona") {
            this.goToNewPersona();
          }
        });

        this.tourStepsReady = true;
        this.startPersonaTourWhenLoaded();
      }, 50);
    }

    if (this.router.url == "/persona") {
      this.router.navigate(["/settings"], { queryParams: { tab: "Persona" } });
    }

    try {
      this.loadingQuota = true;
      this.personaAiApiService.getPersonaQuota().subscribe(
        (response: any) => {
          if (isUserPersonaQuota(response?.result)) {
            this.userPersonaQuota = response.result;
          }
          this.loadingQuota = false;
          this.startPersonaTourWhenLoaded();
        },
        (error) => {
          this.loadingQuota = false;
          this.startPersonaTourWhenLoaded();
        },
        () => {
          this.loadingQuota = false;
          this.startPersonaTourWhenLoaded();
        }
      );
    } catch (error) {
      this.loadingQuota = false;
      this.loadingService.stop(this.loaderId);
    }

    try {
      this.loading = true;
      this.loadingGetUsers = true;
      this.loadingService.start(this.loaderId);
      this.dataService
        .get({
          url: "v2/user-persona",
          data: { disabled: true },
          isLoader: false,
        })
        .subscribe(
          (response: any) => {
            if (response?.result) {
              this.personas = response.result.data;
              this.dataSource.data = this.personas;
              this.orderUserPersona();
            }
            this.loadingGetUsers = false;
            this.startPersonaTourWhenLoaded();
          },
          (error) => {
            this.loadingGetUsers = false;
            setTimeout(() => {
              this.loadingService.stop(this.loaderId);
            }, 200);
            this.startPersonaTourWhenLoaded();
          },
          () => {
            this.loadingGetUsers = false;
            setTimeout(() => {
              this.loadingService.stop(this.loaderId);
            }, 200);
            this.startPersonaTourWhenLoaded();
          }
        );
    } catch (error) {
      this.loadingService.stop(this.loaderId);
      this.loadingGetUsers = false;
    }
  }

  handlePageChange(event: VngPageEvent) {
    this.currentPage = event.page;
    this.itemsPerPage = event.pageSize;
    this.updatePagedRows();
  }

  // R8 off-by-one guard: currentPage is 1-based (matches VngPageEvent.page),
  // so the array-offset math below subtracts 1 before multiplying by
  // pageSize. VngTableDataSource.filteredData is already filtered+sorted but
  // NOT paginated (its own internal .paginator is 0-based, like
  // MatTableDataSource's — deliberately unused here to avoid mixing the two
  // conventions, per 03-component-mapping.md R8).
  private updatePagedRows(): void {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    this.pagedRows = this.dataSource.filteredData.slice(start, start + this.itemsPerPage);
  }

  validatePersonaQuota(): boolean {
    // Unknown quota (request failed or malformed answer): do not block
    // client-side — the server refuses an over-limit create and the add page
    // surfaces that (issue #591).
    if (!this.userPersonaQuota) {
      return true;
    }

    if (this.userPersonaQuota.remaining < 1) {
      switch (this.userPersonaQuota.quota) {
        case 1:
          this.dialog.openDialogComponent(
            WarningDialogComponent,
            {
              type: "persona",
              showTheStarter: false,
              error: "maxNumberOfPersonasFree",
            },
            "800px"
          );
          return false;
        case 3:
          this.dialog.openDialogComponent(
            WarningDialogComponent,
            {
              type: "persona",
              showTheStarter: false,
              pricing: "salesPro",
              selectedPlan: "salesPro",
              error: "maxNumberOfPersonasGrowth",
            },
            "800px"
          );
          return false;
        case 7:
          this.notification.toast(
            "You've hit the maximum number of 7 personas for your current plan. Update or delete any existent persona.",
            "OK",
            7000
          );
          return false;
        default:
          // A plan size without dedicated copy (e.g. quota 0) that is used up
          // gets the generic plan-limit upsell rather than a silent pass.
          this.dialog.openDialogComponent(
            WarningDialogComponent,
            {
              type: "persona",
              showTheStarter: false,
              pricing: "salesPro",
              selectedPlan: "salesPro",
              error: "maxNumberOfPersonas",
            },
            "800px"
          );
          return false;
      }
    }

    return true;
  }

  goToNewPersona(): void {
    if (this.global.onboardingActive) {
      // Clear first: this IS the persona action, so the end$ handler must not
      // run it a second time when the click below ends the tour.
      this.tourExitAction = null;
      this.tourService.end();
    }

    if (this.validatePersonaQuota()) {
      this.router.navigate(["/persona/new"]);
    }
  }

  /**
   * Starts step13 once the persona list has actually rendered.
   *
   * It used to fire on a blind 1s timer from ngOnInit. When `v2/user-persona`
   * resolved after that, the rows (or the empty-state illustration) pushed the
   * page down *underneath* an already-placed step: vng-tour had scrolled the
   * anchor into view and cut its backdrop hole against a rect that no longer
   * existed, so the page ended up auto-scrolled and the spotlight sat off the
   * "Add Persona" button. vng-tour only recomputes on scroll/resize, and there
   * is no public reposition — hence waiting for the data, then one settle pass
   * against a synthetic resize for anything that lands later (illustrations,
   * fonts).
   */
  private startPersonaTourWhenLoaded(): void {
    if (!this.tourStepsReady || this.tourStarted) return;
    if (this.loadingGetUsers || this.loadingQuota) return;

    this.tourStarted = true;
    requestAnimationFrame(() => {
      this.tourService.startAt("step13");
      setTimeout(() => window.dispatchEvent(new Event("resize")), 400);
    });
  }

  goToUserPersona(event: any, userPersona: UserPersona): void {
    if (userPersona.disabled && !this.validatePersonaQuota()) {
      return;
    }

    this.router.navigate([`/persona/${userPersona.id}`]);
  }

  duplicatePersona(event: any, userPersona: UserPersona): void {
    if (userPersona.disabled && !this.validatePersonaQuota()) {
      return;
    }

    this.router.navigate([`/persona/${userPersona.id}/duplicate`]);
  }

  deletePersona(event: any, userPersona: UserPersona): void {
    this.dialog
      .confirm({
        title: "Do you want to <strong>delete</strong> this persona?",
        message: "Deleted items cannot be restored.",
        variant: "danger",
        confirmLabel: "Delete",
      })
      .subscribe((confirmed) => confirmed && this.delete(userPersona));
  }

  setDefaultPersonaConfirm(event: any, userPersona: UserPersona): void {
    if (userPersona.disabled && !this.validatePersonaQuota()) {
      return;
    }

    this.dialog
      .confirm({
        title: `${userPersona.is_default ? "Unset" : "Set"} <strong>${
          userPersona.name
        }</strong> as the default persona?`,
      })
      .subscribe((confirmed) => confirmed && this.setDefaultPersona(userPersona));
  }

  setDefaultPersona(userPersona: UserPersona): void {
    this.dataService
      .put({
        url: `v2/user-persona/set-default/${userPersona.id}`,
        isLoader: true,
        data: {
          is_default: !userPersona.is_default,
        },
      })
      .subscribe(
        (response: any) => {
          this.personas = this.personas.map((persona) => ({
            ...persona,
            is_default: userPersona.is_default ? false : persona.id === userPersona.id,
          }));
          this.dataSource.data = this.personas;
          this.updatePagedRows();
        },
        () => {
          this.updating = false;
        }
      );
  }

  delete(userPersona: UserPersona): void {
    this.dataService.delete({ url: `v2/user-persona/${userPersona.id}`, isLoader: true }).subscribe(
      (response: any) => {
        this.personas = this.personas.filter((persona) => persona.id !== userPersona.id);
        this.dataSource.data = this.personas;
        this.updatePagedRows();
      },
      () => {
        this.updating = false;
      }
    );
  }

  searchUserPersona() {
    this.resetFilterPredicate();
    this.dataSource.filter = this.search.trim().toLowerCase();
    this.currentPage = 1;
    this.updatePagedRows();
  }

  resetFilterPredicate() {
    this.dataSource.filterPredicate = (data: any, filter: string) => {
      const dataStr = Object.keys(data)
        .reduce((currentTerm, key) => {
          return currentTerm + (data as { [key: string]: any })[key] + " ";
        }, "")
        .toLowerCase();
      return dataStr.indexOf(filter) !== -1;
    };
  }

  orderUserPersona() {
    this.dataSource.setSort(this.order ?? "created_at", "asc");
    this.updatePagedRows();
  }
}
